import React, { useState } from 'react';
import { Modal } from '../Modal';
import { supabase } from '../../../config/supabase';
import { cn } from '../../../utils/cn';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const AGE_OPTIONS = ['Under 25', '25-34', '35-49', '50+', 'Prefer not to say'];
const EXPERIENCE_OPTIONS = [
  'Brand new to investing',
  'Beginner — I know the basics',
  'Intermediate — I invest regularly',
  'Advanced — I actively trade',
  'Prefer not to say',
];
const REFERRAL_OPTIONS = [
  'Google', 'Word of Mouth', 'Reddit', 'TikTok',
  'Instagram', 'Facebook', 'X', 'Discord',
  'LinkedIn', 'Youtube', 'DLE Directory', 'Other', 'Prefer not to say',
];

interface OnboardingSurveyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const OnboardingSurveyModal: React.FC<OnboardingSurveyModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [ageGroup, setAgeGroup] = useState<string | null>(null);
  const [investingExperience, setInvestingExperience] = useState<string | null>(null);
  const [referralSource, setReferralSource] = useState<string | null>(null);
  const [otherText, setOtherText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const allAnswered = !!ageGroup && !!investingExperience && !!referralSource;

  const submit = async () => {
    if (!allAnswered) return;
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      await fetch(`${API_BASE_URL}/api/survey/onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          ageGroup,
          investingExperience,
          referralSource,
          ...(referralSource === 'Other' && otherText.trim() ? { referralSourceOther: otherText.trim() } : {}),
        }),
      });
    } catch {
      // Silently fail — don't block user
    } finally {
      setSubmitting(false);
      sessionStorage.setItem('wsle:onboarding-survey-done', '1');
      setSubmitted(true);
      setTimeout(onClose, 1500);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      animationStyle="slide-up"
      className="max-w-md"
      closeOnOverlayClick={false}
      closeOnEscape={false}
      showCloseButton={false}
    >
      <div className="px-1 pt-2 pb-1">
        {submitted ? (
          <div className="py-8 text-center">
            <h2 className="font-serif text-xl font-bold text-foreground">
              Thank you for helping us improve Wallstreetle!
            </h2>
          </div>
        ) : (<>
        <h2 className="font-serif text-xl font-bold text-center text-foreground">
          Please help us improve Wallstreetle by filling out this quick survey
        </h2>
        <p className="text-sm text-muted-foreground text-center mt-1 mb-5">
          Wallstreetle is rapidly growing and we want to improve the game to support our users
        </p>

        {/* Age Group */}
        <div className="mb-5">
          <p className="text-sm font-medium text-foreground mb-2">What age group are you in?</p>
          <div className="flex flex-wrap gap-2">
            {AGE_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setAgeGroup(opt)}
                className={cn(
                  'px-3 py-1.5 rounded-full text-sm border transition-colors',
                  ageGroup === opt
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-gray-50 dark:bg-gray-800 text-foreground border-border shadow-sm hover:border-clue-green-400'
                )}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        {/* Investing Experience */}
        <div className="mb-6">
          <p className="text-sm font-medium text-foreground mb-2">What would you say your investing experience level is?</p>
          <div className="flex flex-wrap gap-2">
            {EXPERIENCE_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setInvestingExperience(opt)}
                className={cn(
                  'px-3 py-1.5 rounded-full text-sm border transition-colors',
                  investingExperience === opt
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-gray-50 dark:bg-gray-800 text-foreground border-border shadow-sm hover:border-clue-green-400'
                )}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        {/* Referral Source */}
        <div className="mb-6">
          <p className="text-sm font-medium text-foreground mb-2">How did you hear about us?</p>
          <div className="flex flex-wrap gap-2">
            {REFERRAL_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  setReferralSource(opt);
                  if (opt !== 'Other') setOtherText('');
                }}
                className={cn(
                  'px-3 py-1.5 rounded-full text-sm border transition-colors',
                  referralSource === opt
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-gray-50 dark:bg-gray-800 text-foreground border-border shadow-sm hover:border-clue-green-400'
                )}
              >
                {opt}
              </button>
            ))}
          </div>
          {referralSource === 'Other' && (
            <input
              type="text"
              value={otherText}
              onChange={(e) => setOtherText(e.target.value.slice(0, 200))}
              placeholder="Please specify..."
              className="mt-3 w-full px-3 py-2 rounded-lg text-sm border border-border bg-gray-50 dark:bg-gray-800 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
              maxLength={200}
            />
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => submit()}
            disabled={submitting || !allAnswered}
            className={cn(
              'px-10 py-2.5 rounded-lg text-sm font-medium text-white bg-blue-600 transition-colors',
              submitting || !allAnswered
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-blue-700'
            )}
          >
            Submit
          </button>
        </div>
        </>)}
      </div>
    </Modal>
  );
};
